import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { NavItem, NavLink } from "reactstrap";

const useOutsideClick = (ref, handler) => {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const SaveButtonDropdown = ({
  mainLabel,
  mainOnClick,
  options = [],
  disabled = false,
}) => {
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, minWidth: 0 });

  const computePosition = useCallback(() => {
    const toggle = toggleRef.current;
    if (!toggle) return;

    const rect = toggle.getBoundingClientRect();
    const minWidth = Math.max(rect.width, 150);
    const top = rect.bottom + 4;
    const left = Math.max(8, rect.right - minWidth);

    setMenuStyle({ top: Math.round(top), left: Math.round(left), minWidth: Math.round(minWidth) });
  }, []);

  // reposition on open, scroll or resize
  useEffect(() => {
    if (!open) return;
    computePosition();
    const onScroll = () => computePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, computePosition]);

  useOutsideClick(menuRef, () => setOpen(false));

  const toggleOpen = () => {
    if (disabled) return;
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      requestAnimationFrame(computePosition);
    }
  };

  // Render the portal menu
  const portalMenu = open ? ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="saveButtonPortalMenu dropdown-menu show"
      style={{
        position: "fixed",
        top: menuStyle.top,
        left: menuStyle.left,
        minWidth: menuStyle.minWidth,
        zIndex: 2000,
      }}
      role="menu"
    >
      {options.map((opt, idx) => (
        <NavItem key={idx}>
          <NavLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (disabled) return;
              opt.onClick && opt.onClick();
              setOpen(false);
            }}
            className={disabled ? "disabled" : ""}
          >
            {opt.label}
          </NavLink>
        </NavItem>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className="saveButtonDropdownContainer btn-group">
        <button
          title={mainLabel}
          aria-disabled={disabled}
          className="button btn-defaultPrimary btnSaveButtonDropdown btnMakeDefinite"
          onClick={() => {
            if (disabled) return;
            mainOnClick && mainOnClick();
          }}
          disabled={disabled}
        >
          <div className="saveButtonDropdownText">{mainLabel}</div>
        </button>

        <div>
          <button
            ref={toggleRef}
            type="button"
            className="saveButton btn-defaultPrimary saveButtonDropdown-toggle dropdown-toggle"
            aria-haspopup="true"
            aria-expanded={open}
            aria-disabled={disabled}
            onClick={toggleOpen}
            disabled={disabled}
          >
            <span className="caret" />
            <span className="sr-only">Toggle Dropdown</span>
          </button>
        </div>
      </div>

      {portalMenu}
    </>
  );
};

export default SaveButtonDropdown;
